import React, { useMemo, useState } from 'react';
import { Search, Plus, Filter, ArrowUpDown, BrainCircuit, RotateCwIcon, CheckCheckIcon, DownloadIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Header } from '../components/Header';
import { copy } from '../copy';
import { InventoryTable } from '../components/discovery/InventoryTable';
type BandFilter = 'all' | 'red' | 'amber' | 'green';
export function FilesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [transcriptionFilter, setTranscriptionFilter] = useState('all');
  const [bandFilter, setBandFilter] = useState<BandFilter>('all');
  const [reviewFilter, setReviewFilter] = useState('all');
  const records = copy.inventory.records;
  const categories = copy.files.categories;
  const filtered = useMemo(() => {
    return records.filter(r => {
      if (searchQuery && !r.name.toLowerCase().includes(searchQuery.toLowerCase()) && !r.description.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (typeFilter !== 'all' && r.type !== typeFilter) return false;
      if (transcriptionFilter !== 'all' && r.transcription !== transcriptionFilter) return false;
      if (reviewFilter !== 'all' && r.reviewStatus !== reviewFilter) return false;
      if (bandFilter !== 'all') {
        const c = r.confidence;
        if (c === null || c === undefined) return false;
        if (bandFilter === 'red' && c >= 40) return false;
        if (bandFilter === 'amber' && (c < 40 || c >= 70)) return false;
        if (bandFilter === 'green' && c < 70) return false;
      }
      return true;
    });
  }, [records, searchQuery, typeFilter, transcriptionFilter, reviewFilter, bandFilter]);
  const toggleSelect = (id: string) => setSelectedFiles(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelectedFiles(prev => prev.length === filtered.length ? [] : filtered.map(f => f.id));
  const f = copy.inventory.filters;
  const selectClass = 'rounded-xl border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] px-3 py-2.5 text-sm text-slate-600 dark:text-[#a3a3a3] outline-none focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-blue-400/20 focus:border-blue-500 dark:focus:border-blue-400 transition-all';
  return <div className="min-h-screen bg-white dark:bg-[#1a1a1a] text-slate-900 dark:text-[#e5e5e5] transition-colors duration-200" data-id="element-311">
      <Header title="Back to Case" minimal showBackArrow backTo="/case" data-id="element-312" />

      <main className="mx-auto max-w-7xl px-6 py-12" data-id="element-313">
        <div className="flex items-center justify-between mb-8" data-id="element-314">
          <div className="flex flex-col" data-id="element-315">
            <h1 className="text-4xl font-bold text-slate-900 dark:text-white tracking-tight" data-id="element-316">
              {copy.files.title}
            </h1>
            <p className="text-base text-slate-500 dark:text-[#a3a3a3] mt-1" data-id="element-317">
              {copy.files.subtitle}
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-318">
            <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl font-semibold transition-all active:scale-95 ${showFilters ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white shadow-md' : 'border-slate-200 dark:border-[#333] text-slate-600 dark:text-[#a3a3a3] hover:bg-slate-50 dark:hover:bg-white/5 hover:border-slate-300 dark:hover:border-[#444]'}`} data-id="element-319">
              <Filter size={18} strokeWidth={2.5} data-id="element-320" />
              {copy.files.filterButton}
            </button>
            <button className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-semibold hover:bg-slate-800 dark:hover:bg-slate-100 active:scale-95 transition-all shadow-sm hover:shadow-md" data-id="element-321">
              <Plus size={18} strokeWidth={2.5} data-id="element-322" />
              {copy.files.addFilesButton}
            </button>
          </div>
        </div>

        <AnimatePresence data-id="element-323">
          {showFilters && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="overflow-hidden mb-8" data-id="element-324">
              <div className="p-6 bg-slate-50 dark:bg-[#222] border border-slate-200 dark:border-[#333] rounded-3xl space-y-6 shadow-sm" data-id="element-325">
                <div data-id="element-326">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2" data-id="element-327">
                    <BrainCircuit size={17} className="text-blue-500" strokeWidth={2} data-id="element-328" />
                    {copy.files.aiCategoriesTitle}
                  </h3>
                  <div className="flex flex-wrap gap-2" data-id="element-329">
                    {categories.map((cat, i) => <button key={i} className="px-4 py-2 rounded-xl border border-slate-200 dark:border-[#333] bg-white dark:bg-[#1a1a1a] text-sm font-medium text-slate-600 dark:text-[#a3a3a3] hover:border-blue-500 dark:hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/10 active:scale-95 transition-all" data-id="element-330">
                        {cat}
                      </button>)}
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-331">
                  <label className="flex flex-col gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-[#555]" data-id="element-332">
                    {f.type}
                    <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className={selectClass} data-id="element-333">
                      <option value="all" data-id="element-334">{f.all}</option>
                      {['Doc', 'Image', 'Audio', 'Video', 'Other'].map(t => <option key={t} value={t} data-id="element-335">
                          {t}
                        </option>)}
                    </select>
                  </label>
                  <label className="flex flex-col gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-[#555]" data-id="element-336">
                    {f.transcription}
                    <select value={transcriptionFilter} onChange={e => setTranscriptionFilter(e.target.value)} className={selectClass} data-id="element-337">
                      <option value="all" data-id="element-338">{f.all}</option>
                      {['N/A', 'Queued', 'Transcribed', 'Flagged', 'Failed'].map(t => <option key={t} value={t} data-id="element-339">
                          {t}
                        </option>)}
                    </select>
                  </label>
                  <label className="flex flex-col gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-[#555]" data-id="element-340">
                    {f.confidence}
                    <select value={bandFilter} onChange={e => setBandFilter(e.target.value as BandFilter)} className={selectClass} data-id="element-341">
                      <option value="all" data-id="element-342">{f.all}</option>
                      <option value="red" data-id="element-343">Red (&lt;40)</option>
                      <option value="amber" data-id="element-344">Amber (40–69)</option>
                      <option value="green" data-id="element-345">Green (70–100)</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-[#555]" data-id="element-346">
                    {f.reviewStatus}
                    <select value={reviewFilter} onChange={e => setReviewFilter(e.target.value)} className={selectClass} data-id="element-347">
                      <option value="all" data-id="element-348">{f.all}</option>
                      {['Unreviewed', 'Reviewed', 'Follow-up'].map(t => <option key={t} value={t} data-id="element-349">
                          {t}
                        </option>)}
                    </select>
                  </label>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        <div className="flex items-center gap-4 mb-6" data-id="element-350">
          <div className="relative flex-1" data-id="element-351">
            <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-slate-400 dark:text-[#555]" data-id="element-352">
              <Search size={20} data-id="element-353" />
            </div>
            <input type="text" placeholder={copy.files.searchPlaceholder} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-[#222] border border-slate-200 dark:border-[#333] rounded-2xl outline-none focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-blue-400/20 focus:border-blue-500 dark:focus:border-blue-400 transition-all text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-[#555]" data-id="element-354" />
          </div>
          <button className="p-4 rounded-2xl border border-slate-200 dark:border-[#333] text-slate-500 dark:text-[#a3a3a3] hover:bg-slate-50 dark:hover:bg-white/5 hover:border-slate-300 dark:hover:border-[#444] active:scale-95 transition-all" data-id="element-355">
            <ArrowUpDown size={20} data-id="element-356" />
          </button>
        </div>

        {selectedFiles.length > 0 && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex flex-wrap items-center justify-between gap-3 p-4 mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl shadow-sm" data-id="element-357">
            <span className="text-sm font-semibold text-blue-700 dark:text-blue-300" data-id="element-358">
              {selectedFiles.length} {copy.files.selectedSuffix}
            </span>
            <div className="flex items-center gap-2" data-id="element-359">
              <button className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-800/40 rounded-xl active:scale-95 transition-all" data-id="element-360">
                <RotateCwIcon size={14} data-id="element-361" />
                {copy.inventory.bulk.rerun}
              </button>
              <button className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-800/40 rounded-xl active:scale-95 transition-all" data-id="element-362">
                <CheckCheckIcon size={14} data-id="element-363" />
                {copy.inventory.bulk.markReviewed}
              </button>
              <button className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-800/40 rounded-xl active:scale-95 transition-all" data-id="element-364">
                <DownloadIcon size={14} data-id="element-365" />
                {copy.inventory.bulk.exportSelection}
              </button>
            </div>
          </motion.div>}

        <InventoryTable records={filtered} selected={selectedFiles} onToggle={toggleSelect} onToggleAll={toggleSelectAll} data-id="element-366" />
        {filtered.length === 0 && <p className="mt-8 text-center text-slate-500 dark:text-[#a3a3a3] py-12 border-2 border-dashed border-slate-200 dark:border-[#262626] rounded-3xl bg-slate-50/50 dark:bg-[#222]/50" data-id="element-367">
            {copy.files.emptySearch}
          </p>}
      </main>
    </div>;
}